import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesHierarchyLevel, WesTimeGrain } from '../useWesPalletStagingFilters';
import WesPressable from './WesPressable';

const HIERARCHY_OPTIONS: { key: WesHierarchyLevel; label: string }[] = [
  { key: 'region', label: 'Region / Unit' },
  { key: 'market', label: 'Market / POD' },
  { key: 'location', label: 'Location' },
];

const TIMEFRAME_OPTIONS: { key: WesTimeGrain; label: string }[] = [
  { key: 'year', label: 'Year' },
  { key: 'period', label: 'Period' },
  { key: 'week', label: 'Week' },
];

interface WesChartConfigToggleProps {
  hierarchy: WesHierarchyLevel;
  timeframe: WesTimeGrain;
  onHierarchyChange: (value: WesHierarchyLevel) => void;
  onTimeframeChange: (value: WesTimeGrain) => void;
}

interface WesChartConfigProps extends WesChartConfigToggleProps {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

// The two segmented toggles, reused inside the collapsible desktop card and the mobile sheet.
export const WesChartConfigToggles: React.FC<WesChartConfigToggleProps> = ({
  hierarchy,
  timeframe,
  onHierarchyChange,
  onTimeframeChange,
}) => (
  <View style={styles.body}>
    <View style={styles.group}>
      <Text style={styles.groupLabel}>Location Hierarchy</Text>
      <View style={styles.segmented}>
        {HIERARCHY_OPTIONS.map((option) => {
          const isActive = hierarchy === option.key;
          return (
            <WesPressable
              key={option.key}
              style={[styles.segment, isActive && styles.segmentActive]}
              onPress={() => onHierarchyChange(option.key)}
              accessibilityState={{ selected: isActive }}
              testID={`wes-chart-hierarchy-${option.key}`}
            >
              {({ hovered }) => (
                <Text style={[styles.segmentText, isActive && styles.segmentTextActive, hovered && !isActive && styles.segmentTextHover]}>{option.label}</Text>
              )}
            </WesPressable>
          );
        })}
      </View>
    </View>
    <View style={styles.group}>
      <Text style={styles.groupLabel}>Timeframe</Text>
      <View style={styles.segmented}>
        {TIMEFRAME_OPTIONS.map((option) => {
          const isActive = timeframe === option.key;
          return (
            <WesPressable
              key={option.key}
              style={[styles.segment, isActive && styles.segmentActive]}
              onPress={() => onTimeframeChange(option.key)}
              accessibilityState={{ selected: isActive }}
              testID={`wes-chart-timeframe-${option.key}`}
            >
              {({ hovered }) => (
                <Text style={[styles.segmentText, isActive && styles.segmentTextActive, hovered && !isActive && styles.segmentTextHover]}>{option.label}</Text>
              )}
            </WesPressable>
          );
        })}
      </View>
    </View>
  </View>
);

// Collapsible desktop card, shown on the Executive Overview tab only. Layout copied from the Crewing chart configuration card.
const WesChartConfig: React.FC<WesChartConfigProps> = ({ expanded: expandedProp, onExpandedChange, ...props }) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const toggleExpanded = () => {
    const next = !expanded;
    setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.card} testID="wes-chart-config">
        <WesPressable
          style={styles.header}
          hoverStyle={styles.headerHover}
          onPress={toggleExpanded}
          accessibilityRole="button"
          accessibilityLabel="Chart Configuration"
          accessibilityState={{ expanded }}
          testID="wes-chart-config-toggle"
        >
          <View style={[styles.chevron, expanded && styles.chevronOpen]}>
            <MaterialIcons name="chevron-right" size={22} color="#034895" />
          </View>
          <View style={styles.titleRow}>
            <Text style={styles.title}>Chart Configuration</Text>
            <View style={styles.countPill}>
              <Text style={styles.countText}>2 / 2</Text>
            </View>
            <Text style={styles.hint} numberOfLines={1}>Grouping and timeframe for all charts</Text>
          </View>
        </WesPressable>
        {expanded ? <WesChartConfigToggles {...props} /> : null}
      </View>
    </View>
  );
};

// Style values copied from CrewingAnalyticsDashboard (chartConfigCard, chartConfigHeader, segmentedControl).
const styles = StyleSheet.create({
  wrap: { paddingHorizontal: 20, paddingBottom: 16 },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#005EC0',
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 10, borderRadius: 6 },
  headerHover: { backgroundColor: '#E7EFFB' },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' },
  title: { fontSize: 13, fontWeight: '700', fontFamily: FontFamily.bold, color: '#1A1D21' },
  countPill: { backgroundColor: '#005CBC', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999 },
  countText: { fontSize: 11, fontFamily: FontFamily.medium, color: '#FFFFFF' },
  hint: { fontSize: 12, fontFamily: FontFamily.regular, color: '#838383', flexShrink: 1 },
  chevron: { width: 22, height: 22, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  chevronOpen: { transform: [{ rotate: '90deg' }] },
  body: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    columnGap: 32,
    rowGap: 16,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginHorizontal: 8,
    paddingTop: 16,
  },
  group: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  groupLabel: { fontSize: 11, fontWeight: '700', fontFamily: FontFamily.bold, color: '#616161', letterSpacing: 0.4, textTransform: 'uppercase' },
  segmented: { flexDirection: 'row', backgroundColor: '#EFF5FC', borderRadius: 8, padding: 3, columnGap: 2 },
  segment: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 6, backgroundColor: 'transparent' },
  segmentActive: { backgroundColor: '#005CBC' },
  segmentText: { fontSize: 12.5, fontWeight: '600', fontFamily: FontFamily.medium, color: '#616161' },
  segmentTextActive: { color: '#FFFFFF', fontFamily: FontFamily.bold },
  segmentTextHover: { color: '#005CBC' },
});

export default WesChartConfig;
